import { PointerEvent as ReactPointerEvent, ReactNode, useEffect, useRef, useState } from 'react';

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
};

type DraggableDetailPanelProps = {
  /** Conteúdo da barra superior (área de arraste). Botões internos devem usar stopPropagation no pointerdown. */
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Reseta a posição ao trocar de item. */
  resetKey?: string | number;
  className?: string;
};

/**
 * Painel lateral de detalhes arrastável pela barra de título.
 * Sem overlay — permanece sobre o conteúdo da lista.
 */
export function DraggableDetailPanel({
  header,
  children,
  footer,
  resetKey,
  className = '',
}: DraggableDetailPanelProps) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<DragState | null>(null);

  useEffect(() => {
    setOffset({ x: 0, y: 0 });
    dragRef.current = null;
  }, [resetKey]);

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select, [data-no-drag]')) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    setOffset({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore if capture already released
    }
  }

  return (
    <aside
      className={`fixed bottom-4 right-4 top-[7.5rem] z-30 flex w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-xl ${className}`}
      style={{
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        willChange: 'transform',
      }}
      role="dialog"
      aria-modal="false"
    >
      <div
        className="shrink-0 cursor-grab select-none active:cursor-grabbing"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        title="Arraste para reposicionar"
      >
        {header}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
      {footer ? <div className="shrink-0 border-t border-secondary-200 bg-white">{footer}</div> : null}
    </aside>
  );
}
